import axios from 'axios';
import { generateFileProgressEvent } from '../../../common/FileProgress';
import * as types from '../state/types';
import { ReduxPromiseChain } from '../../../common/ReduxPromiseChain';
import { MappingTypeEnum } from '../../../common/Constants/Enumeration/MappingTypeEnum';
import * as ImportStudentActions from '../state/actions';

export class ImportStudentsApi {
  constructor(baseUrl, config) {
    this.baseUrl = baseUrl;
    this.config = config;
  }

  /**
   * Using the ReduxPromiseChain process a list
   * of promises. When all have resolved map the request
   * value to either a dispatched action based on the name
   * of the action or use a function for more control.
   *
   * @param {funtion} dispatch
   */
  loadAllAPIRequest = async (dispatch, importTypeId) => {
    const promiseChain = new ReduxPromiseChain({
      dispatch,
      catchFunc: (err) => {
        console.error('API Error', err);
      },
    });

    // List of Promises to resolve
    promiseChain.promises = [
      this.getLanguages(),
      this.getGrades(),
      this.getMappingData(MappingTypeEnum.Language, importTypeId), // language mapping
      this.getMappingData(MappingTypeEnum.MealStatus, importTypeId), // meals status mapping
      this.getMappingData(MappingTypeEnum.Site, importTypeId), // site mapping
      this.getMappingData(MappingTypeEnum.Grade, importTypeId), // site mapping

      true, // flag to run dispatch function, might move to complete?
      this.getHeaderDetails(importTypeId), // 1 = Default & 2 = Auto

      this.getImportSettings(),
    ];

    // Map each promise resolve to a dispatch action or function.
    // Uses the index of each array to map. 0 => 0
    promiseChain.dispatchMap = [
      types.GET_LANGUAGES,
      types.SET_GRADES,
      (response) => {
        dispatch({
          type: types.GET_LANGUAGE_MAPPING_DATA,
          payload: response.data,
        });
      },
      (response) => {
        dispatch({
          type: types.GET_MEAL_MAPPING_DATA,
          payload: response.data,
        });
      },
      (response) => {
        dispatch(
          ImportStudentActions.setSiteMappingDataAction(response.data.Data)
        );
      },
      (response) => {
        dispatch(
          ImportStudentActions.setGradeMappingDataAction(response.data.Data)
        );
      },
      types.IMPORTSTUDENT_CLEAR_DATA,
      types.IMPORTSTUDENT_HEADER,
      types.GET_IMPORTSTUDENT_SETTINGS,
    ];

    if (importTypeId > 0) {
      promiseChain.promises.push(
        this.getImportStudentsConfiguration(importTypeId),
        this.getImportSchedule(importTypeId)
      );
      promiseChain.dispatchMap.push(
        types.IMPORTSTUDENT_GET_CONFIG,
        types.GET_IMPORT_SCHEDULE
      );
    }

    if (importTypeId === 0) {
      await dispatch({ type: types.CLEAR_CUSTOM_MAPPING_DATA });
      await dispatch({
        type: types.IMPORTSTUDENT_GET_CONFIG,
        payload: {
          data: {
            Data: {
              Id: 0,
              Description: 'AUTO',
              FileName: 'C:\\',
              FileFormat: 0,
              Delimiter: ',',
              IsFirstRowHeader: true,
              EnrolledValue: 'A',
              EnrollStatus: null,
              WithdrawnValue: 'D',
              ImportTypeId: 0,
              UseMappedValue: false,
              DC_DefaultCaseType: '',
              DC_ImportTypeId: 0,
              IsWithdrawNotInFile: false,
              FileEncoding: 0,
              Schedule: 'Sunday, Monday, Tuesday, Wednesday, Thursday, Friday',
            },
            Meta: {},
            Errors: [],
          },
        },
      });
      await dispatch({
        type: types.GET_IMPORT_SCHEDULE,
        payload: {
          data: {
            Data: {
              Title: 'Success',
              Message: 'Retreived import schedule',
              Scenario: null,
              Data: {
                Hour: 3,
                Minute: 0,
                AMorPM: 'AM',
                DaysOfTheWeek: [0, 1, 2, 3, 4, 5],
              },
            },
            Meta: {},
            Errors: [],
          },
        },
      });
    }

    return await promiseChain.start();
  };

  genericAxiosPromise = (url, config = this.config) => {
    return new Promise((resolve, reject) => {
      axios
        .get(url, this.config)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  postImportStudentsFileData(formData, dispatch) {
    const config = {
      ...this.config,
      headers: {
        ...this.config.headers,
        'content-type': 'multipart/form-data',
      },
      onUploadProgress: generateFileProgressEvent(dispatch),
    };

    const url = `${this.baseUrl}/api/import`;

    return new Promise((resolve, reject) => {
      axios
        .post(url, formData, config)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  postImportStudents(obj) {
    const url = `${this.baseUrl}/api/studentconfig`;
    return new Promise((resolve, reject) => {
      axios
        .post(url, obj, this.config)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  importStudents(obj, dispatch) {
    const url = `${this.baseUrl}/api/student/import`;
    const config = {
      ...this.config,
      onUploadProgress: generateFileProgressEvent(dispatch),
    };

    return new Promise((resolve, reject) => {
      axios
        .post(url, obj, config)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  getImportStudentsConfiguration(id) {
    return this.genericAxiosPromise(
      `${this.baseUrl}/api/ImportStudentHeader/${id}`
    );
  }

  getMappingData(id, headerId) {
    return this.genericAxiosPromise(
      `${this.baseUrl}/api/importmapping/${id}/${headerId}`
    );
  }

  getHeaderDetails(id) {
    return this.genericAxiosPromise(
      `${this.baseUrl}/api/ImportStudentDetail/${id}`
    );
  }

  getLanguages() {
    return this.genericAxiosPromise(`${this.baseUrl}/api/language`);
  }

  getGrades() {
    return this.genericAxiosPromise(`${this.baseUrl}/api/grade`);
  }

  getImportSchedule(id) {
    return this.genericAxiosPromise(
      `${this.baseUrl}/api/ControlInfo/ImportSchedule/${id}`
    );
  }

  getImportSettings() {
    return this.genericAxiosPromise(
      `${this.baseUrl}/api/StudentConfig/AutoImports`
    );
  }

  deleteImportSettings(id) {
    const url = `${this.baseUrl}/api/StudentConfig/Delete/${id}`;

    return new Promise((resolve, reject) => {
      axios
        .post(url, id, this.config)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
}



// WEBPACK FOOTER //
// ./src/pages/importstudents/api/ImportStudentsApi.js